import { forwardRef } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";
import { Input as SemanticInput } from "semantic-ui-react";
import {
  Input as DSInput,
  InputGroup,
} from "@hireart/component-library";
import "./input.scss";

/**
 * Adapter wrapping the design system Input with a SUI-compatible API so the
 * `semantic-ui-react` dependency can be retired without touching consumers
 * (EE-504, phase 2h). Mirrors the established Checkbox/Radio adapters.
 *
 * SUI signature:  onChange(event, { value, name, ...rest })
 * DS  signature:  onChange(event)
 *
 * Most of the barrel's prop surface (value, onChange's 2-arg form, placeholder,
 * disabled, name, type, fluid, error, and React-element `icon`) routes to the
 * DS Input/InputGroup. Three SUI-only addon APIs that the DS Input cannot
 * reproduce mechanically — children-as-input (`<Input><NumericFormat/></Input>`),
 * `action`, and `label`/`labelPosition` — plus the icon-font *string* form
 * (`icon="search"`) fall through to a TEMPORARY SemanticInput. Those consumers
 * migrate in the EE-504 follow-up slices (EE-1142/1145/1149/1150), after which
 * the SemanticInput fallback is removed (EE-1152).
 */
const usesSemanticOnlyApi = ({ children, action, label, labelPosition, icon }) =>
  children != null ||
  action != null ||
  label != null ||
  labelPosition != null ||
  typeof icon === "string";

export const Input = forwardRef(({ className = "", ...props }, ref) => {
  if (usesSemanticOnlyApi(props)) {
    return (
      <SemanticInput
        {...props}
        ref={ref}
        className={clsx("ha-input", className)}
      />
    );
  }

  const {
    onChange,
    name,
    fluid,
    error,
    icon,
    iconPosition,
    disabled,
    ...rest
  } = props;

  // Re-emit the SUI 2-arg signature so consumers reading `data.value` /
  // `data.name` keep working; `?.` guards the no-onChange case.
  // `...rest` is spread first so that the explicit `value` key (fresh from the
  // DOM event) wins over any stale controlled `value` prop still in `rest`.
  const handleChange = (event) =>
    onChange?.(event, { ...rest, name, value: event.target.value });

  const input = (
    <DSInput
      {...rest}
      ref={ref}
      name={name}
      isDisabled={disabled}
      isInvalid={Boolean(error) || undefined}
      onChange={handleChange}
      className={clsx("ha-input", fluid && "w-full", className)}
    />
  );

  if (icon == null) return input;

  return (
    <InputGroup className={clsx(fluid && "w-full")}>
      {iconPosition === "left" && icon}
      {input}
      {iconPosition !== "left" && icon}
    </InputGroup>
  );
});

Input.displayName = "Input";

Input.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  fluid: PropTypes.bool,
  icon: PropTypes.node,
  iconPosition: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
};
